import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Send, Paperclip, MessageCircle, ArrowLeft, User, Shield } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ScrollArea } from '../components/ui/ScrollArea';
import { Separator } from '../components/ui/Separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/Dialog';
import { Label } from '../components/ui/Label';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
export function ChatPage() {
  const {
    t
  } = useLanguage();
  const {
    currentUser,
    isAdmin
  } = useAuth();
  const {
    sendMessage,
    getConversationMessages
  } = useData();
  const navigate = useNavigate();
  const conversationId = currentUser ? `conv_${currentUser.id}` : '';
  const messages = getConversationMessages(conversationId);
  const [input, setInput] = useState('');
  const [attachName, setAttachName] = useState('');
  const [attachUrl, setAttachUrl] = useState('');
  const [attachOpen, setAttachOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages.length]);
  // Firestore real-time listeners handle message updates automatically — no polling needed
  if (!currentUser) {
    return <div className="w-full min-h-screen flex flex-col" data-id="element-709">
        <div className="flex-1 flex items-center justify-center p-8" data-id="element-710">
          <Card className="max-w-sm w-full text-center" data-id="element-711">
            <CardContent className="py-12 space-y-4" data-id="element-712">
              <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto" data-id="element-713" />
              <p className="text-muted-foreground" data-id="element-714">
                {t('chat.login.required')}
              </p>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate('/auth')} data-id="element-715">
                {t('nav.login')}
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer data-id="element-716" />
      </div>;
  }
  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage({
      senderId: currentUser.id,
      senderName: currentUser.fullName,
      senderType: 'user',
      receiverId: 'admin',
      message: input.trim(),
      conversationId
    });
    setInput('');
  };
  const handleSendAttachment = () => {
    if (!attachName.trim()) return;
    sendMessage({
      senderId: currentUser.id,
      senderName: currentUser.fullName,
      senderType: 'user',
      receiverId: 'admin',
      message: `📎 Sent a file: ${attachName}`,
      attachment: {
        name: attachName,
        url: attachUrl || '#'
      },
      conversationId
    });
    setAttachName('');
    setAttachUrl('');
    setAttachOpen(false);
    toast.success('File sent!');
  };
  return <div className="w-full min-h-screen flex flex-col" data-id="element-717">
      <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 py-6" data-id="element-718">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-4" data-id="element-719">
          <Card data-id="element-720">
            <CardContent className="p-4 flex items-center gap-4" data-id="element-721">
              <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center" data-id="element-722">
                <Shield className="h-5 w-5 text-white" data-id="element-723" />
              </div>
              <div className="flex-1" data-id="element-724">
                <h3 className="font-heading font-semibold text-sm" data-id="element-725">
                  {t('chat.admin.name')}
                </h3>
                <div className="flex items-center gap-1.5" data-id="element-726">
                  <div className={`h-2 w-2 rounded-full ${isAdmin ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-727" />
                  <span className="text-xs text-muted-foreground" data-id="element-728">
                    {isAdmin ? t('chat.online') : t('chat.offline')}
                  </span>
                </div>
              </div>
              <Badge variant="secondary" className="text-xs" data-id="element-729">
                {messages.length} messages
              </Badge>
            </CardContent>
          </Card>
        </motion.div>

        {/* Messages */}
        <Card className="flex-1 flex flex-col min-h-[400px]" data-id="element-730">
          <ScrollArea className="flex-1 p-4" data-id="element-731">
            {messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center py-16 text-center" data-id="element-732">
                <MessageCircle className="h-12 w-12 text-muted-foreground mb-4" data-id="element-733" />
                <p className="text-muted-foreground text-sm" data-id="element-734">
                  {t('chat.empty')}
                </p>
              </div> : <div className="space-y-3" data-id="element-735">
                <AnimatePresence data-id="element-736">
                  {messages.map((msg, i) => <motion.div key={msg.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: i * 0.02
              }} className={`flex ${msg.senderType === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-737">
                      <div className={`flex items-end gap-2 max-w-[80%] ${msg.senderType === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-738">
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.senderType === 'user' ? 'bg-green-600' : 'bg-blue-600'}`} data-id="element-739">
                          {msg.senderType === 'user' ? <User className="h-3.5 w-3.5 text-white" data-id="element-740" /> : <Shield className="h-3.5 w-3.5 text-white" data-id="element-741" />}
                        </div>
                        <div className={`rounded-2xl px-4 py-2.5 ${msg.senderType === 'user' ? 'bg-blue-600 text-white rounded-br-md' : 'bg-muted rounded-bl-md'}`} data-id="element-742">
                          <p className="text-sm" data-id="element-743">{msg.message}</p>
                          {msg.attachment && <div className={`mt-1.5 flex items-center gap-1 text-xs ${msg.senderType === 'user' ? 'text-blue-200' : 'text-muted-foreground'}`} data-id="element-744">
                              <Paperclip className="h-3 w-3" data-id="element-745" />
                              <span data-id="element-746">{msg.attachment.name}</span>
                            </div>}
                          <p className={`text-[10px] mt-1 ${msg.senderType === 'user' ? 'text-blue-200' : 'text-muted-foreground'}`} data-id="element-747">
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                          </p>
                        </div>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
                <div ref={chatEndRef} data-id="element-748" />
              </div>}
          </ScrollArea>

          <Separator data-id="element-749" />

          {/* Input */}
          <div className="p-3 flex items-center gap-2" data-id="element-750">
            <Dialog open={attachOpen} onOpenChange={setAttachOpen} data-id="element-751">
              <DialogTrigger asChild data-id="element-752">
                <Button variant="ghost" size="icon-sm" data-id="element-753">
                  <Paperclip className="h-4 w-4" data-id="element-754" />
                </Button>
              </DialogTrigger>
              <DialogContent data-id="element-755">
                <DialogHeader data-id="element-756">
                  <DialogTitle data-id="element-757">{t('chat.attach')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2" data-id="element-758">
                  <div className="space-y-2" data-id="element-759">
                    <Label data-id="element-760">File Name</Label>
                    <Input value={attachName} onChange={e => setAttachName(e.target.value)} placeholder="e.g. receipt.pdf" data-id="element-761" />
                  </div>
                  <div className="space-y-2" data-id="element-762">
                    <Label data-id="element-763">File URL (optional)</Label>
                    <Input value={attachUrl} onChange={e => setAttachUrl(e.target.value)} placeholder="https://..." data-id="element-764" />
                  </div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleSendAttachment} data-id="element-765">
                    <Send className="h-4 w-4" data-id="element-766" />
                    {t('chat.send')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Input value={input} onChange={e => setInput(e.target.value)} placeholder={t('chat.placeholder')} onKeyDown={e => e.key === 'Enter' && handleSend()} className="flex-1" data-id="element-767" />
            <Button size="icon" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSend} disabled={!input.trim()} data-id="element-768">
              <Send className="h-4 w-4" data-id="element-769" />
            </Button>
          </div>
        </Card>
      </div>
      <Footer data-id="element-770" />
    </div>;
}